import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { View, Text, Pressable, StyleSheet, Linking } from 'react-native';
import { Check, Mail, Search, Star } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { SegmentedControl } from '../common/SegmentedControl';
import { TextLink } from '../common/TextLink';
import { useAuthStore } from '../../store/authStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useUIStore } from '../../store/uiStore';
import { useVendorsStore } from '../../store/vendorsStore';
import { useMapAreaId } from '../../hooks/useMapAreaId';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { planBundleOpportunityUrl } from '../../utils/share';
import { suggestVendors, vendorsForArea, type VendorSuggestion } from '../../utils/vendorSuggest';
import { vendorNameKey, isNationalVendor } from '../../utils/vendors';
import { vendorInviteMailto } from '../../utils/vendorInvite';
import { ratableBids, ratingSummaryText, summarizeRatings } from '../../utils/vendorRatings';
import type { RatingSummary } from '../../utils/vendorRatings';
import {
  repChanges,
  repDraft,
  vendorChanges,
  vendorDraft,
  type RepDraft,
  type VendorDraft,
} from '../../utils/vendorEdits';
import { VENDOR_CATEGORIES, canonicalCategory } from '../../constants/vendorCategories';
import {
  MAX_BUNDLE_INVITED_VENDORS,
  MAX_VENDOR_EDIT_NOTE,
  MAX_VENDOR_RATING_NOTE,
} from '../../schemas/firestoreSchemas';
import type {
  DefensePlanFeature,
  PlanBid,
  PlanBundleInvitedVendor,
  Vendor,
  VendorEdit,
  VendorRating,
  VendorRep,
} from '../../types';

// Find vendors for one bundle (Nov 2026, Glenn's vendor sheet). The owner
// shortlists vendors from the shared directory, then emails each one the
// opportunity link from their own mail program. A shortlisted vendor is not
// on the team: it joins only by bidding and being accepted, so a rep's
// address reaches the plan only when the rep gives it. Anyone signed in can
// suggest a vendor or another contact at a listed one; a moderator approves
// it before anyone else sees it. Hosted like the bids view: a CompanionPanel
// on the desktop shell, DefensePlanVendorsSheet below it.

const NO_FEATURES: DefensePlanFeature[] = [];
const NO_INVITES: PlanBundleInvitedVendor[] = [];
const NO_BIDS: PlanBid[] = [];
const MAX_CATEGORIES = 12;
const MAX_CATEGORY_LEN = 60;

type OpenForm = { vendorId: string; kind: 'rate' | 'edit' };

const shortDate = (ms: number) =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(ms));

const toast = (message: string) => useUIStore.getState().showToast({ message });

/** An ink checkbox: the choice shows as a check, never as a color. */
function Checkbox({ checked }: { checked: boolean }) {
  const { colors } = useTheme();
  return (
    <View
      style={[
        styles.checkbox,
        {
          borderColor: checked ? colors.text : colors.textTertiary,
          backgroundColor: checked ? colors.text : 'transparent',
        },
      ]}>
      {checked && <Check size={12} color={colors.background} />}
    </View>
  );
}

function repLine(rep: VendorRep | undefined): string | null {
  if (!rep) return null;
  return rep.role ? `${rep.name}, ${rep.role}` : rep.name;
}

function contactLine(rep: VendorRep | undefined): string | null {
  if (!rep) return null;
  return [rep.email, rep.phone].filter(Boolean).join(' · ') || null;
}

function matchesSearch(s: VendorSuggestion, q: string): boolean {
  if (!q) return true;
  const hay = [s.vendor.organization, ...s.vendor.categories, s.rep?.name ?? '', s.rep?.role ?? '']
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

function VendorRow({
  suggestion,
  invited,
  why,
  disabled,
  onToggle,
  summary,
  canRate,
  hasRating,
  open,
  onOpen,
  children,
}: {
  suggestion: VendorSuggestion;
  invited: boolean;
  why: string;
  disabled: boolean;
  onToggle: () => void;
  summary: RatingSummary | undefined;
  canRate: boolean;
  hasRating: boolean;
  open: 'rate' | 'edit' | null;
  onOpen: (kind: 'rate' | 'edit') => void;
  /** The inline form, shown directly under the row while one is open. */
  children?: ReactNode;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover(!disabled);
  const { vendor, rep } = suggestion;
  const person = repLine(rep);
  const contact = contactLine(rep);
  return (
    <View style={styles.rowGroup}>
      <View
        style={[
          styles.row,
          { borderColor: colors.border },
          open && { backgroundColor: colors.pressedSurface },
        ]}>
        <Pressable
          onPress={onToggle}
          disabled={disabled}
          testID={`bundle-vendor-${vendor.id}`}
          accessibilityRole="checkbox"
          aria-checked={invited}
          accessibilityLabel={`${invited ? 'Remove' : 'Invite'} ${vendor.organization}`}
          {...hoverProps}
          style={[
            styles.rowPress,
            hovered && { backgroundColor: colors.hoverSurface },
            !disabled && CURSOR_POINTER,
          ]}>
          <Checkbox checked={invited} />
          <View style={styles.rowBody}>
            <Text style={[styles.rowTitle, { color: colors.text }]}>{vendor.organization}</Text>
            <Text style={[styles.meta, { color: colors.textSecondary }]}>{why}</Text>
            {summary && (
              <Text
                style={[styles.meta, { color: colors.textSecondary }]}
                testID={`bundle-vendor-${vendor.id}-rating`}>
                {ratingSummaryText(summary)}
              </Text>
            )}
            {person && <Text style={[styles.meta, { color: colors.text }]}>{person}</Text>}
            {contact && (
              <Text style={[styles.meta, { color: colors.textSecondary }]} selectable>
                {contact}
              </Text>
            )}
          </View>
        </Pressable>
        <View style={styles.rowActionsIndented}>
          {canRate && (
            <Button
              title={hasRating ? 'Change rating' : 'Rate'}
              onPress={() => onOpen('rate')}
              variant="secondary"
              size="xs"
              testID={`bundle-vendor-${vendor.id}-rate`}
            />
          )}
          <Button
            title="Suggest a change"
            onPress={() => onOpen('edit')}
            variant="secondary"
            size="xs"
            testID={`bundle-vendor-${vendor.id}-suggest-change`}
          />
        </View>
      </View>
      {children}
    </View>
  );
}

/** Five ink stars. Filled up to the value, outlined after. */
function StarPicker({ value, onChange }: { value: number; onChange: (stars: number) => void }) {
  const { colors } = useTheme();
  const desktop = useDesktopShell();
  const size = desktop ? 32 : 44;
  return (
    <View accessibilityRole="radiogroup" aria-label="Rating" style={styles.stars}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Pressable
          key={n}
          onPress={() => onChange(n)}
          testID={`vendor-rate-star-${n}`}
          accessibilityRole="radio"
          aria-checked={value === n}
          accessibilityLabel={n === 1 ? '1 star' : `${n} stars`}
          style={[styles.star, { width: size, height: size }, CURSOR_POINTER]}>
          <Star size={20} color={colors.text} fill={n <= value ? colors.text : 'none'} />
        </Pressable>
      ))}
    </View>
  );
}

function RateForm({
  vendor,
  planId,
  candidates,
  myRating,
  onDone,
}: {
  vendor: Vendor;
  planId: string;
  candidates: PlanBid[];
  myRating: VendorRating | undefined;
  onDone: () => void;
}) {
  const { colors } = useTheme();
  const rateVendor = useVendorsStore((s) => s.rateVendor);
  const deleteRating = useVendorsStore((s) => s.deleteRating);
  const [stars, setStars] = useState(myRating?.stars ?? 0);
  const [note, setNote] = useState(myRating?.note ?? '');
  const [bidId, setBidId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A bid already tied to this vendor is the only choice that stays valid.
  const pool = useMemo(() => {
    const linked = candidates.filter((b) => b.vendorId === vendor.id);
    return linked.length > 0 ? linked : candidates;
  }, [candidates, vendor.id]);
  const chosen = myRating ? null : (pool.find((b) => b.id === bidId) ?? pool[0] ?? null);
  const canSave = stars > 0 && !saving && (!!myRating || !!chosen);

  const handleSave = async () => {
    const ref = myRating
      ? { planId: myRating.planId, bidId: myRating.bidId, link: false }
      : chosen
        ? { planId, bidId: chosen.id, link: !chosen.vendorId }
        : null;
    if (!ref) return;
    setSaving(true);
    setError(null);
    try {
      await rateVendor({
        vendorId: vendor.id,
        planId: ref.planId,
        bidId: ref.bidId,
        stars,
        note: note.trim() || undefined,
        linkBid: ref.link,
      });
      toast('Rating saved');
      onDone();
    } catch {
      setError("Couldn't save your rating. Try again.");
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!myRating) return;
    setSaving(true);
    setError(null);
    try {
      await deleteRating(myRating.id);
      toast('Rating removed');
      onDone();
    } catch {
      setError("Couldn't remove your rating. Try again.");
      setSaving(false);
    }
  };

  return (
    <View style={[styles.inlineForm, { borderColor: colors.border }]} testID="vendor-rate-form">
      <Text style={[styles.rowTitle, { color: colors.text }]}>{`Rate ${vendor.organization}`}</Text>
      {myRating?.hidden && (
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          A moderator hid this rating, so only you can see it.
        </Text>
      )}
      {chosen && pool.length === 1 && (
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          {`For the bid from ${chosen.organization} on ${chosen.bundleName}`}
        </Text>
      )}
      {chosen && pool.length > 1 && (
        <View style={styles.pickerGroup}>
          <Text style={[styles.label, { color: colors.textSecondary }]}>
            Which accepted bid was theirs?
          </Text>
          <View accessibilityRole="radiogroup" aria-label="Accepted bid" style={styles.list}>
            {pool.map((b) => {
              const on = b.id === chosen.id;
              return (
                <Pressable
                  key={b.id}
                  onPress={() => setBidId(b.id)}
                  testID={`vendor-rate-bid-${b.id}`}
                  accessibilityRole="radio"
                  aria-checked={on}
                  style={[
                    styles.choice,
                    { borderColor: on ? colors.text : colors.border },
                    CURSOR_POINTER,
                  ]}>
                  <View style={styles.choiceCheck}>
                    {on && <Check size={14} color={colors.text} />}
                  </View>
                  <Text style={[styles.meta, styles.choiceText, { color: colors.text }]}>
                    {`${b.organization} · ${b.bundleName}`}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}
      {chosen && !chosen.vendorId && (
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          {`Saving ties this bid to ${vendor.organization}. You can't move it to another vendor later.`}
        </Text>
      )}
      <StarPicker value={stars} onChange={setStars} />
      <Input
        compact
        label="Note (optional)"
        value={note}
        onChangeText={setNote}
        maxLength={MAX_VENDOR_RATING_NOTE}
        multiline
        testID="vendor-rate-note"
      />
      {error && (
        <Text style={[styles.meta, { color: colors.danger }]} testID="vendor-rate-error">
          {error}
        </Text>
      )}
      <View style={styles.footer}>
        <Button
          title="Cancel"
          onPress={onDone}
          variant="secondary"
          size="sm"
          disabled={saving}
          style={styles.half}
          testID="vendor-rate-cancel"
        />
        <Button
          title="Save"
          onPress={() => void handleSave()}
          size="sm"
          disabled={!canSave}
          loading={saving}
          style={styles.half}
          testID="vendor-rate-save"
        />
      </View>
      {myRating && (
        <View style={styles.suggestLink}>
          <TextLink
            label="Remove my rating"
            onPress={() => void handleRemove()}
            textStyle={TYPOGRAPHY.caption}
            testID="vendor-rate-remove"
          />
        </View>
      )}
    </View>
  );
}

/** Splits "Tree Service, Landscaping" on commas or semicolons. */
function parseCategories(text: string): string[] {
  return text
    .split(/[,;]/)
    .map((c) => c.trim().slice(0, MAX_CATEGORY_LEN))
    .filter(Boolean);
}

function EditForm({
  suggestion,
  areaId,
  onDone,
}: {
  suggestion: VendorSuggestion;
  areaId: string | null;
  onDone: () => void;
}) {
  const { colors } = useTheme();
  const suggestEdit = useVendorsStore((s) => s.suggestEdit);
  const { vendor, rep } = suggestion;
  const [target, setTarget] = useState<'company' | 'contact'>('company');
  const [company, setCompany] = useState<VendorDraft>(() => vendorDraft(vendor));
  const [categoriesText, setCategoriesText] = useState(() => vendor.categories.join(', '));
  const [contact, setContact] = useState<RepDraft | null>(() => (rep ? repDraft(rep) : null));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = useMemo(() => parseCategories(categoriesText), [categoriesText]);
  const tooMany = categories.length > MAX_CATEGORIES;
  const editingContact = target === 'contact' && !!rep && !!contact;

  const changes = useMemo(
    () =>
      editingContact && rep && contact
        ? repChanges(rep, contact)
        : vendorChanges(vendor, { ...company, categories }, areaId),
    [editingContact, rep, contact, vendor, company, categories, areaId]
  );
  const hasChanges = Object.keys(changes).length > 0;
  const canSend = hasChanges && !saving && (editingContact || !tooMany);

  const setCompanyField = (patch: Partial<VendorDraft>) =>
    setCompany((prev) => ({ ...prev, ...patch }));
  const setContactField = (patch: Partial<RepDraft>) =>
    setContact((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleSend = async () => {
    setSaving(true);
    setError(null);
    try {
      await suggestEdit({
        vendorId: vendor.id,
        repId: editingContact ? rep?.id : undefined,
        changes,
        note: note.trim() || undefined,
      });
      toast('Thanks. A moderator will check your change.');
      onDone();
    } catch {
      setError("Couldn't send that. Try again.");
      setSaving(false);
    }
  };

  return (
    <View style={[styles.inlineForm, { borderColor: colors.border }]} testID="vendor-edit-form">
      <Text style={[styles.rowTitle, { color: colors.text }]}>
        {`Suggest a change to ${vendor.organization}`}
      </Text>
      {rep && contact && (
        <View style={styles.pickerGroup}>
          <Text style={[styles.label, { color: colors.textSecondary }]}>What to fix</Text>
          <SegmentedControl
            options={[
              { key: 'company', label: 'Company' },
              { key: 'contact', label: 'Contact' },
            ]}
            value={target}
            onChange={setTarget}
            ariaLabel="What to fix"
            role="radiogroup"
            testID="vendor-edit-target"
          />
        </View>
      )}
      {editingContact && contact ? (
        <>
          <Input
            compact
            label="Name"
            value={contact.name}
            onChangeText={(name) => setContactField({ name })}
            maxLength={120}
            testID="vendor-edit-rep-name"
          />
          <Input
            compact
            label="Role"
            value={contact.role}
            onChangeText={(role) => setContactField({ role })}
            maxLength={100}
            testID="vendor-edit-rep-role"
          />
          <Input
            compact
            label="Email"
            value={contact.email}
            onChangeText={(email) => setContactField({ email })}
            maxLength={254}
            keyboardType="email-address"
            autoCapitalize="none"
            testID="vendor-edit-rep-email"
          />
          <Input
            compact
            label="Phone"
            value={contact.phone}
            onChangeText={(phone) => setContactField({ phone })}
            maxLength={40}
            keyboardType="phone-pad"
            testID="vendor-edit-rep-phone"
          />
        </>
      ) : (
        <>
          <Input
            compact
            label="Name"
            value={company.organization}
            onChangeText={(organization) => setCompanyField({ organization })}
            maxLength={200}
            testID="vendor-edit-org"
          />
          <Input
            compact
            label="Website"
            value={company.website}
            onChangeText={(website) => setCompanyField({ website })}
            maxLength={300}
            autoCapitalize="none"
            testID="vendor-edit-website"
          />
          <Input
            compact
            label="Kinds of work"
            value={categoriesText}
            onChangeText={setCategoriesText}
            hint="Separate with commas."
            error={tooMany ? `Up to ${MAX_CATEGORIES} kinds of work.` : undefined}
            testID="vendor-edit-categories"
          />
          {areaId && (
            <View style={styles.pickerGroup}>
              <Text style={[styles.label, { color: colors.textSecondary }]}>Works</Text>
              <SegmentedControl
                options={[
                  { key: 'here', label: 'Around here' },
                  { key: 'everywhere', label: 'Everywhere' },
                ]}
                value={company.scope}
                onChange={(scope) => setCompanyField({ scope })}
                ariaLabel="Where this vendor works"
                role="radiogroup"
                testID="vendor-edit-works"
              />
            </View>
          )}
          <Input
            compact
            label="Licenses"
            value={company.licenses}
            onChangeText={(licenses) => setCompanyField({ licenses })}
            maxLength={300}
            testID="vendor-edit-licenses"
          />
          <Input
            compact
            label="Notes"
            value={company.notes}
            onChangeText={(notes) => setCompanyField({ notes })}
            maxLength={1000}
            multiline
            testID="vendor-edit-notes"
          />
        </>
      )}
      <Input
        compact
        label="Why (optional)"
        value={note}
        onChangeText={setNote}
        maxLength={MAX_VENDOR_EDIT_NOTE}
        testID="vendor-edit-note"
      />
      {error && (
        <Text style={[styles.meta, { color: colors.danger }]} testID="vendor-edit-error">
          {error}
        </Text>
      )}
      <View style={styles.footer}>
        <Button
          title="Cancel"
          onPress={onDone}
          variant="secondary"
          size="sm"
          disabled={saving}
          style={styles.half}
          testID="vendor-edit-cancel"
        />
        <Button
          title="Send"
          onPress={() => void handleSend()}
          size="sm"
          disabled={!canSend}
          loading={saving}
          style={styles.half}
          testID="vendor-edit-save"
        />
      </View>
    </View>
  );
}

function InvitedRow({
  entry,
  rep,
  busy,
  onInvite,
  onRemove,
}: {
  entry: PlanBundleInvitedVendor;
  rep: VendorRep | undefined;
  busy: boolean;
  onInvite: () => void;
  onRemove: () => void;
}) {
  const { colors } = useTheme();
  const name = entry.repName ? `${entry.organization} · ${entry.repName}` : entry.organization;
  const status = entry.invitedAt
    ? `Emailed ${shortDate(entry.invitedAt.toMillis())}`
    : rep?.email
      ? 'Not emailed yet'
      : 'No email listed. Call or send the link another way.';
  return (
    <View
      style={[styles.invitedRow, { borderColor: colors.border }]}
      testID={`bundle-invited-${entry.vendorId}`}>
      <Text style={[styles.rowTitle, { color: colors.text }]}>{name}</Text>
      <Text style={[styles.meta, { color: colors.textSecondary }]}>{status}</Text>
      {rep?.phone && !rep.email && (
        <Text style={[styles.meta, { color: colors.textSecondary }]} selectable>
          {rep.phone}
        </Text>
      )}
      <View style={styles.rowActions}>
        {rep?.email ? (
          <Button
            title={entry.invitedAt ? 'Email again' : 'Email invite'}
            onPress={onInvite}
            variant="secondary"
            size="xs"
            leftIcon={Mail}
            disabled={busy}
            testID={`bundle-invited-${entry.vendorId}-email`}
          />
        ) : null}
        <Button
          title="Remove"
          onPress={onRemove}
          variant="secondary"
          size="xs"
          disabled={busy}
          testID={`bundle-invited-${entry.vendorId}-remove`}
        />
      </View>
    </View>
  );
}

function SuggestVendorForm({
  areaId,
  vendors,
  onDone,
}: {
  areaId: string | null;
  vendors: Vendor[];
  onDone: () => void;
}) {
  const { colors } = useTheme();
  const suggestVendor = useVendorsStore((s) => s.suggestVendor);
  const suggestRep = useVendorsStore((s) => s.suggestRep);
  const [organization, setOrganization] = useState('');
  const [website, setWebsite] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [other, setOther] = useState('');
  const [works, setWorks] = useState<'here' | 'everywhere'>(areaId ? 'here' : 'everywhere');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A company already in the directory gets a new contact, not a second copy.
  const existing = useMemo(() => {
    const key = organization.trim() ? vendorNameKey(organization) : '';
    return key ? (vendors.find((v) => v.nameKey === key && v.status === 'approved') ?? null) : null;
  }, [organization, vendors]);

  const categories = [
    ...picked,
    ...other
      .split(/[;,]/)
      .map((c) => canonicalCategory(c))
      .filter(Boolean),
  ];
  const areaIds = works === 'here' && areaId ? [areaId] : ['*'];
  const canSave =
    organization.trim().length > 0 &&
    !saving &&
    (existing ? name.trim().length > 0 : categories.length > 0);

  const toggleCategory = (label: string) =>
    setPicked((prev) =>
      prev.includes(label) ? prev.filter((c) => c !== label) : [...prev, label]
    );

  const rep = name.trim()
    ? {
        name: name.trim(),
        role: role.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      }
    : undefined;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const ok = existing
      ? rep && (await suggestRep(existing.id, { ...rep, areaIds }))
      : await suggestVendor({
          organization: organization.trim(),
          website: website.trim() || undefined,
          categories: categories.slice(0, 12),
          areaIds,
          rep,
        });
    setSaving(false);
    if (!ok) {
      setError("Couldn't send that. Try again.");
      return;
    }
    toast('Thanks. A moderator checks suggestions before others see them.');
    onDone();
  };

  return (
    <View style={styles.form} testID="bundle-vendor-suggest-form">
      <Input
        compact
        label="Company"
        value={organization}
        onChangeText={setOrganization}
        placeholder="e.g. Pine Ridge Tree Care"
        testID="bundle-vendor-suggest-org"
      />
      {existing ? (
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          {`${existing.organization} is already listed. Add a contact there and a moderator will check it.`}
        </Text>
      ) : (
        <>
          <Input
            compact
            label="Website"
            value={website}
            onChangeText={setWebsite}
            placeholder="e.g. pineridgetree.com"
            autoCapitalize="none"
            testID="bundle-vendor-suggest-website"
          />
          <Text style={[styles.label, { color: colors.textSecondary }]}>Kinds of work</Text>
          <View style={styles.chips}>
            {VENDOR_CATEGORIES.map(({ label }) => {
              const on = picked.includes(label);
              return (
                <Pressable
                  key={label}
                  onPress={() => toggleCategory(label)}
                  accessibilityRole="checkbox"
                  aria-checked={on}
                  accessibilityLabel={label}
                  style={[
                    styles.chip,
                    { borderColor: on ? colors.text : colors.border },
                    CURSOR_POINTER,
                  ]}>
                  {on && <Check size={12} color={colors.text} />}
                  <Text style={[styles.chipText, { color: colors.text }]}>{label}</Text>
                </Pressable>
              );
            })}
          </View>
          <Input
            compact
            label="Other kinds of work"
            value={other}
            onChangeText={setOther}
            placeholder="e.g. Chipping; Roof cleaning"
            testID="bundle-vendor-suggest-other"
          />
          {areaId && (
            <>
              <Text style={[styles.label, { color: colors.textSecondary }]}>Works</Text>
              <SegmentedControl
                options={[
                  { key: 'here', label: 'Around here' },
                  { key: 'everywhere', label: 'Everywhere' },
                ]}
                value={works}
                onChange={setWorks}
                ariaLabel="Where this vendor works"
                role="radiogroup"
                testID="bundle-vendor-suggest-works"
              />
            </>
          )}
        </>
      )}
      <Input
        compact
        label="Contact name"
        value={name}
        onChangeText={setName}
        placeholder="e.g. Jane Smith"
        testID="bundle-vendor-suggest-name"
      />
      <Input
        compact
        label="Role"
        value={role}
        onChangeText={setRole}
        placeholder="e.g. Owner"
        testID="bundle-vendor-suggest-role"
      />
      <Input
        compact
        label="Email"
        value={email}
        onChangeText={setEmail}
        placeholder="e.g. jane@example.com"
        keyboardType="email-address"
        autoCapitalize="none"
        testID="bundle-vendor-suggest-email"
      />
      <Input
        compact
        label="Phone"
        value={phone}
        onChangeText={setPhone}
        keyboardType="phone-pad"
        testID="bundle-vendor-suggest-phone"
      />
      {error && <Text style={[styles.meta, { color: colors.danger }]}>{error}</Text>}
      <View style={styles.footer}>
        <Button
          title="Cancel"
          onPress={onDone}
          variant="secondary"
          size="sm"
          disabled={saving}
          style={styles.half}
          testID="bundle-vendor-suggest-cancel"
        />
        <Button
          title="Send suggestion"
          onPress={() => void handleSave()}
          variant="primary"
          size="sm"
          disabled={!canSave}
          loading={saving}
          style={styles.half}
          testID="bundle-vendor-suggest-save"
        />
      </View>
    </View>
  );
}

export function BundleVendorsView({
  planId,
  bundleId,
  highlightVendorIds,
  hideTitle = false,
}: {
  planId: string;
  bundleId: string;
  /** Vendors the weekly brief named for this bundle (Nov 2026): listed first
   *  under "Suggested for this work" and labeled, never ticked for the owner. */
  highlightVendorIds?: string[];
  hideTitle?: boolean;
}) {
  const { colors } = useTheme();
  const uid = useAuthStore((s) => s.user?.id ?? null);
  const isModerator = useAuthStore((s) => s.isModerator);
  const plan = useDefensePlansStore((s) => (s.ownPlan?.id === planId ? s.ownPlan : null));
  const toggleInvitedVendor = useDefensePlansStore((s) => s.toggleInvitedVendor);
  const markVendorInvited = useDefensePlansStore((s) => s.markVendorInvited);
  const setBundleBidsOpen = useDefensePlansStore((s) => s.setBundleBidsOpen);
  const start = useVendorsStore((s) => s.start);
  const vendors = useVendorsStore((s) => s.vendors);
  const reps = useVendorsStore((s) => s.reps);
  const ratings = useVendorsStore((s) => s.ratings);
  const edits = useVendorsStore((s) => s.edits);
  const withdrawEdit = useVendorsStore((s) => s.withdrawEdit);
  const bids = useDefensePlansStore((s) => s.bidsByPlan[planId] ?? NO_BIDS);
  const isLoaded = useVendorsStore((s) => s.isLoaded);
  const mapAreaId = useMapAreaId();
  const [search, setSearch] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openForm, setOpenForm] = useState<OpenForm | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);

  useEffect(() => {
    if (uid) start(uid, isModerator);
  }, [uid, isModerator, start]);

  const bundle = plan?.bundles.find((b) => b.id === bundleId) ?? null;
  const features = plan?.features ?? NO_FEATURES;
  const invites = bundle?.invitedVendors ?? NO_INVITES;
  // Where the plan is, else where the map is. Never shown: areas are backend-only.
  const areaId =
    plan && plan.areaId !== 'unassigned'
      ? plan.areaId
      : mapAreaId !== 'unassigned'
        ? mapAreaId
        : null;

  const strategyIds = useMemo(
    () => [...new Set(features.filter((f) => f.bundleId === bundleId).map((f) => f.strategy))],
    [features, bundleId]
  );
  const highlightKey = (highlightVendorIds ?? []).join(',');
  const suggested = useMemo(() => {
    const list = areaId ? suggestVendors(vendors, reps, { areaId, strategyIds }) : [];
    const order = highlightKey ? highlightKey.split(',') : [];
    if (order.length === 0) return list;
    // The brief's picks first, in its order; a pick the directory no longer
    // suggests here (unapproved, or the bundle's work changed) is left out.
    const rank = (id: string) => {
      const i = order.indexOf(id);
      return i === -1 ? order.length : i;
    };
    return [...list].sort((a, b) => rank(a.vendor.id) - rank(b.vendor.id));
  }, [vendors, reps, areaId, strategyIds, highlightKey]);
  const others = useMemo(() => {
    const shown = new Set(suggested.map((s) => s.vendor.id));
    const all = areaId
      ? vendorsForArea(vendors, reps, areaId)
      : vendorsForArea(vendors, reps, '*').filter((s) => isNationalVendor(s.vendor));
    return all.filter((s) => !shown.has(s.vendor.id));
  }, [vendors, reps, areaId, suggested]);
  const mine = useMemo(
    () => vendors.filter((v) => v.status === 'suggested' && v.createdBy === uid),
    [vendors, uid]
  );
  const myEdits = useMemo(
    () => edits.filter((e) => e.status === 'suggested' && e.createdBy === uid),
    [edits, uid]
  );
  const summaries = useMemo(() => summarizeRatings(ratings), [ratings]);
  const myRatings = useMemo(() => {
    const map = new Map<string, VendorRating>();
    for (const r of ratings) if (r.createdBy === uid) map.set(r.vendorId, r);
    return map;
  }, [ratings, uid]);

  if (!plan || !bundle) {
    return (
      <View style={styles.content}>
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          This bundle is no longer on the plan.
        </Text>
      </View>
    );
  }

  const q = search.trim().toLowerCase();
  const invitedIds = new Set(invites.map((i) => i.vendorId));
  const full = invites.length >= MAX_BUNDLE_INVITED_VENDORS;
  const repById = (id: string | undefined) => (id ? reps.find((r) => r.id === id) : undefined);

  // Accepted bids that could back a rating of this vendor. The shortlist is
  // any bundle of this plan where the owner invited the vendor.
  const candidatesFor = (vendor: Vendor): PlanBid[] =>
    ratableBids(
      vendor,
      bids,
      new Set(
        plan.bundles
          .filter((b) => b.invitedVendors?.some((i) => i.vendorId === vendor.id))
          .map((b) => b.id)
      )
    );

  const handleWithdraw = async (edit: VendorEdit) => {
    setWithdrawing(edit.id);
    try {
      await withdrawEdit(edit.id);
    } catch {
      toast("Couldn't withdraw that. Try again.");
    } finally {
      setWithdrawing(null);
    }
  };

  const toggle = (s: VendorSuggestion) =>
    void toggleInvitedVendor(bundleId, {
      vendorId: s.vendor.id,
      repId: s.rep?.id,
      organization: s.vendor.organization,
      repName: s.rep?.name,
    });

  const fromBrief = new Set(highlightKey ? highlightKey.split(',') : []);
  const whyText = (s: VendorSuggestion, suggestion: boolean) => {
    const kinds = (suggestion ? s.matchedCategories : s.vendor.categories).join(', ');
    const reach = s.local ? 'Local' : 'Works everywhere';
    const why = kinds ? `${kinds} · ${reach}` : reach;
    return suggestion && fromBrief.has(s.vendor.id) ? `From this week's brief · ${why}` : why;
  };

  const handleInvite = async (entry: PlanBundleInvitedVendor) => {
    const rep = repById(entry.repId);
    if (!rep?.email) return;
    setBusy(true);
    try {
      if (!bundle.bidsOpen) {
        const ok = await setBundleBidsOpen(planId, bundleId, true);
        if (!ok) {
          toast("Couldn't open the bundle for bids");
          return;
        }
        toast('Opened for bids so the link works');
      }
      const href = vendorInviteMailto({
        email: rep.email,
        repName: rep.name,
        organization: entry.organization,
        bundleName: bundle.name,
        opportunityUrl: planBundleOpportunityUrl(planId, bundleId),
        ownerName: plan.ownerName,
      });
      await Linking.openURL(href);
      await markVendorInvited(bundleId, entry.vendorId);
    } finally {
      setBusy(false);
    }
  };

  const renderRows = (list: VendorSuggestion[], suggestion: boolean) =>
    list
      .filter((s) => matchesSearch(s, q))
      .map((s) => {
        const myRating = myRatings.get(s.vendor.id);
        const candidates = candidatesFor(s.vendor);
        const open = openForm?.vendorId === s.vendor.id ? openForm.kind : null;
        const close = () => setOpenForm(null);
        return (
          <VendorRow
            key={s.vendor.id}
            suggestion={s}
            invited={invitedIds.has(s.vendor.id)}
            why={whyText(s, suggestion)}
            disabled={full && !invitedIds.has(s.vendor.id)}
            onToggle={() => toggle(s)}
            summary={summaries.get(s.vendor.id)}
            canRate={!!myRating || candidates.length > 0}
            hasRating={!!myRating}
            open={open}
            onOpen={(kind) => setOpenForm({ vendorId: s.vendor.id, kind })}>
            {open === 'rate' && (
              <RateForm
                vendor={s.vendor}
                planId={planId}
                candidates={candidates}
                myRating={myRating}
                onDone={close}
              />
            )}
            {open === 'edit' && <EditForm suggestion={s} areaId={areaId} onDone={close} />}
          </VendorRow>
        );
      });

  const suggestedRows = renderRows(suggested, true);
  const otherRows = renderRows(others, false);

  return (
    <View style={styles.content} testID={`plan-bundle-${bundleId}-vendors`}>
      {!hideTitle && <Text style={[styles.title, { color: colors.text }]}>{bundle.name}</Text>}

      <Text
        style={[styles.overlineFirst, { color: colors.textSecondary }]}
        accessibilityRole="header">
        {invites.length === 0 ? 'Invited' : `Invited (${invites.length})`}
      </Text>
      {invites.length === 0 ? (
        <Text style={[styles.meta, { color: colors.textTertiary }]} testID="bundle-vendors-none">
          Tick vendors below, then email each one the link to bid.
        </Text>
      ) : (
        <View style={styles.list}>
          {plan.visibility !== 'shared' && (
            <Text style={[styles.meta, { color: colors.textSecondary }]}>
              This plan is private, so the link won&apos;t open for vendors until you share it.
            </Text>
          )}
          {invites.map((entry) => (
            <InvitedRow
              key={entry.vendorId}
              entry={entry}
              rep={repById(entry.repId)}
              busy={busy}
              onInvite={() => void handleInvite(entry)}
              onRemove={() =>
                void toggleInvitedVendor(bundleId, {
                  vendorId: entry.vendorId,
                  organization: entry.organization,
                })
              }
            />
          ))}
        </View>
      )}

      <Input
        value={search}
        onChangeText={setSearch}
        onClear={() => setSearch('')}
        leftIcon={Search}
        placeholder="Search vendors"
        accessibilityLabel="Search vendors"
        containerStyle={styles.search}
        testID="bundle-vendors-search"
      />

      {!isLoaded ? (
        <Text style={[styles.meta, { color: colors.textSecondary }]}>Loading...</Text>
      ) : (
        <>
          <Text
            style={[styles.overline, { color: colors.textSecondary }]}
            accessibilityRole="header">
            Suggested for this work
          </Text>
          {suggestedRows.length > 0 ? (
            <View style={styles.list} testID="bundle-vendors-suggested">
              {suggestedRows}
            </View>
          ) : (
            <Text style={[styles.meta, { color: colors.textTertiary }]}>
              {q ? 'No suggested vendor matches.' : 'No listed vendor does this kind of work yet.'}
            </Text>
          )}

          <Text
            style={[styles.overline, { color: colors.textSecondary }]}
            accessibilityRole="header">
            Other vendors
          </Text>
          {otherRows.length > 0 ? (
            <View style={styles.list} testID="bundle-vendors-others">
              {otherRows}
            </View>
          ) : (
            <Text style={[styles.meta, { color: colors.textTertiary }]}>
              {q ? 'No other vendor matches.' : 'None listed yet.'}
            </Text>
          )}

          {(mine.length > 0 || myEdits.length > 0) && (
            <>
              <Text
                style={[styles.overline, { color: colors.textSecondary }]}
                accessibilityRole="header">
                Your suggestions
              </Text>
              {mine.length > 0 && (
                <View style={styles.list} testID="bundle-vendors-mine">
                  {mine.map((v) => (
                    <View key={v.id} style={[styles.invitedRow, { borderColor: colors.border }]}>
                      <Text style={[styles.rowTitle, { color: colors.text }]}>
                        {v.organization}
                      </Text>
                      <Text style={[styles.meta, { color: colors.textSecondary }]}>
                        Waiting for review
                      </Text>
                    </View>
                  ))}
                </View>
              )}
              {myEdits.length > 0 && (
                <View style={styles.list} testID="bundle-vendors-mine-edits">
                  {myEdits.map((edit) => (
                    <View
                      key={edit.id}
                      style={[styles.invitedRow, { borderColor: colors.border }]}
                      testID={`bundle-edit-${edit.id}`}>
                      <Text style={[styles.rowTitle, { color: colors.text }]}>
                        {`Change to ${vendors.find((v) => v.id === edit.vendorId)?.organization ?? 'a vendor'}`}
                      </Text>
                      <Text style={[styles.meta, { color: colors.textSecondary }]}>
                        Waiting for review
                      </Text>
                      <View style={styles.rowActions}>
                        <Button
                          title="Withdraw"
                          onPress={() => void handleWithdraw(edit)}
                          variant="secondary"
                          size="xs"
                          disabled={withdrawing === edit.id}
                          testID={`bundle-edit-${edit.id}-withdraw`}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </>
          )}
        </>
      )}

      {suggesting ? (
        <SuggestVendorForm areaId={areaId} vendors={vendors} onDone={() => setSuggesting(false)} />
      ) : (
        <View style={styles.suggestLink}>
          <TextLink
            label="Suggest a vendor or a contact"
            onPress={() => setSuggesting(true)}
            textStyle={TYPOGRAPHY.caption}
            testID="bundle-vendor-suggest"
          />
        </View>
      )}
      <View testID="plan-vendors-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  overlineFirst: {
    ...TYPOGRAPHY.overline,
  },
  // With the content gap (sm), 24 above the section label and 8 below it.
  overline: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.md,
  },
  label: {
    ...TYPOGRAPHY.caption,
  },
  meta: {
    ...TYPOGRAPHY.caption,
  },
  list: {
    gap: SPACING.xs,
  },
  search: {
    marginTop: SPACING.md,
  },
  rowGroup: {
    gap: SPACING.xs,
  },
  // The Pressable carries the padding so its hover fill reaches the border.
  row: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    overflow: 'hidden',
  },
  rowPress: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.smd,
    paddingTop: SPACING.sm,
    paddingBottom: SPACING.xs,
  },
  // Lines up with the row text: checkbox width (16) plus the row gap.
  rowActionsIndented: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.xs,
    paddingLeft: SPACING.smd + 16 + SPACING.sm,
    paddingRight: SPACING.smd,
    paddingBottom: SPACING.sm,
  },
  inlineForm: {
    gap: SPACING.sm,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    padding: SPACING.smd,
  },
  pickerGroup: {
    gap: SPACING.xs,
  },
  stars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  star: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  choice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xs,
  },
  choiceCheck: {
    width: 16,
    alignItems: 'center',
  },
  choiceText: {
    flex: 1,
    minWidth: 0,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  rowTitle: {
    ...withWeight(TYPOGRAPHY.bodySmall, 'medium'),
  },
  invitedRow: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    gap: 2,
  },
  rowActions: {
    flexDirection: 'row',
    gap: SPACING.xs,
    marginTop: SPACING.xs,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.xs,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  form: {
    gap: SPACING.sm,
    marginTop: SPACING.md,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.xs,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xxs,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xxs,
  },
  chipText: {
    ...TYPOGRAPHY.caption,
  },
  footer: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  half: {
    flex: 1,
  },
  suggestLink: {
    marginTop: SPACING.md,
    alignItems: 'flex-start',
  },
  scrollSentinel: {
    height: 1,
  },
});
